import { mixOklab } from './oklab';
import { PALETTES, type Accent } from './palettes';

/**
 * Die Farbmarken, die das ganze Layout benutzt. Namen und Werte kommen aus `theme()`
 * der Design-Vorlage; die App setzt sie als CSS-Variablen auf `:root`, damit einzelne
 * Bauteile nur noch `var(--card)` schreiben und Modus- und Akzentwechsel ohne
 * Neuberechnung durchschlagen.
 */
export interface Theme {
  bg: string;
  card: string;
  soft: string;
  line: string;
  text: string;
  dim: string;
  dim2: string;
  acc: string;
  accDeep: string;
  accSoft: string;
  onAccSoft: string;
  accGlow: string;
  /**
   * Markierte Wortgruppe in der globalen Bewertung — eigener Dreiklang.
   *
   * ⚠️ **Aus der Palette gerechnet, nicht fest verdrahtet.** Bis zum 2026-09-13 standen hier
   * feste Blautöne; wer Grün oder Rot als Akzent wählte, bekam beim Markieren trotzdem Blau
   * (gemeldet vom Nutzer). Die Marke ist kräftiger als `accSoft`, damit sie im Fliesstext als
   * Markierung auffällt und nicht wie eine blosse Tönung wirkt.
   */
  pickBg: string;
  pickFg: string;
  pickRing: string;
}

export function buildTheme(accent: Accent, dark: boolean): Theme {
  const p = PALETTES[accent] ?? PALETTES.blau;

  return dark
    ? {
        // Vorlage: color-mix(in oklab, base 8%, #0f1216) — hier vorab gerechnet, s. oklab.ts
        bg: mixOklab(p.base, 0.08, '#0f1216'),
        card: '#171b21',
        soft: '#1d222a',
        line: 'rgba(245,247,250,.11)',
        text: '#eef1f5',
        dim: '#98a1ad',
        dim2: '#c3cad4',
        acc: p.base,
        accDeep: '#ffffff',
        accSoft: p.softDark,
        onAccSoft: '#dbe6ff',
        accGlow: p.glow,
        pickBg: mixOklab(p.base, 0.34, '#0f1216'),
        pickFg: '#eef1f5',
        pickRing: mixOklab(p.base, 0.6, '#0f1216'),
      }
    : {
        bg: mixOklab(p.base, 0.07, '#f7f8fa'),
        card: '#ffffff',
        soft: '#f1f3f7',
        line: '#e4e7ec',
        text: '#14171c',
        dim: '#6b7280',
        dim2: '#4b5563',
        acc: p.base,
        accDeep: p.deep,
        accSoft: p.soft,
        onAccSoft: p.deep,
        accGlow: p.glow,
        pickBg: mixOklab(p.base, 0.18, '#ffffff'),
        // Die Textfarbe bleibt die gewöhnliche: ein markiertes Wort soll sich in der Farbe
        // **nicht** vom Satz lösen, sonst liest man die Markierung statt des Textes.
        pickFg: '#14171c',
        pickRing: mixOklab(p.base, 0.38, '#ffffff'),
      };
}

/** Schreibt das Thema als CSS-Variablen an ein Element (in der Regel `document.documentElement`). */
export function applyTheme(target: HTMLElement, theme: Theme): void {
  for (const [key, value] of Object.entries(theme)) {
    target.style.setProperty('--' + key, value);
  }
}
